import { Button } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { FORM_ERROR } from 'final-form';
import React, { useEffect, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification, RFFFieldErrorWrapper } from 'common/components';
import { Typography as T } from 'common/typography';
import validateYupSchema from 'common/utils/validateYupSchema';

import { ValidationSchema } from './ValidationSchema';
import { SigneeInformation, User } from '../../../apis/merchant-onboarding-service/types';
import { BREAKPOINT_MOBILE } from '../constants';

type Props = {
  userInformation?: SigneeInformation;
  onboardingToken: string;
  emailToken: string;
  onSuccess: (emailToken: string) => void;
  onSubmit: (user: User, emailToken: String, onboardingToken: String) => Promise<{ id: string }>;
  onInvalidEmailToken: (onboardingToken: string) => void;
  onUserAlreadySignedUp: () => void;
  validationSchema: ValidationSchema;
};

type FormValues = {
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  phoneNumber: string | undefined;
  termsOfService: boolean;
};

const DEFAULT_VALUES: FormValues = {
  email: undefined,
  firstName: undefined,
  lastName: undefined,
  phoneNumber: undefined,
  termsOfService: false,
};

const Root = styled.div`
  display: flex;
  padding: 90px 16px 167px;
  justify-content: center;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 56px 16px 32px;
  }

  .more-visible-checkbox {
    opacity: ${cssThemedValue({ light: '0.5', dark: '1' })};
  }
`;

const StyledForm = styled.form`
  display: block;
  max-width: 616px;
  width: 100%;
`;

const Heading = styled(T.Heading1)`
  margin-block-end: 16px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    font-size: 32px;
    margin-block-end: 8px;
  }
`;

const Subheading = styled(T.Heading5)`
  color: ${cbSemanticCssVars.textPrimary};
  font-weight: 400;
  margin-block-end: 40px;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    font-size: 16px;
    margin-block-end: 0;
  }
`;

const Headings = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const AgreementDescription = styled.div`
  flex: 1;
  user-select: none;
`;

const Agreement = styled.div`
  display: flex;

  ${AgreementDescription} {
    margin-inline-start: 24px;
  }
`;

const Agreements = styled.div`
  margin: 24px 0;

  ${Agreement} + ${Agreement} {
    margin-block-start: 12px;
  }
`;

export const MerchantSignUpForm = ({
  userInformation,
  onboardingToken,
  emailToken,
  onSubmit,
  onSuccess,
  onInvalidEmailToken,
  onUserAlreadySignedUp,
  validationSchema,
}: Props) => {
  const intl = useIntl();

  useEffect(() => {
    if (userInformation?.hasAlreadySignedUp) {
      return onUserAlreadySignedUp();
    }
  }, [
    userInformation?.hasAlreadySignedUp,
    userInformation?.phoneNumber,
    onUserAlreadySignedUp,
    emailToken,
    onboardingToken,
  ]);

  const handleSubmit = async (formValues: FormValues) => {
    const validValues = validationSchema.validateSync(formValues);
    try {
      const userValues = { country: userInformation?.country, ...validValues };
      await onSubmit(userValues, emailToken, onboardingToken);
    } catch (error) {
      if (error.response?.data?.error === 'invalid_email_token') {
        return onInvalidEmailToken(onboardingToken);
      }
      return {
        [FORM_ERROR]: error,
      };
    }
    onSuccess(emailToken);
    return undefined;
  };

  const initialValues: FormValues = useMemo(() => {
    return {
      ...DEFAULT_VALUES,
      ...userInformation,
      termsOfService: false,
    };
  }, [userInformation]);

  return (
    <Root>
      <Form<FormValues>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={validateYupSchema(validationSchema)}
      >
        {({ handleSubmit, hasValidationErrors, submitting, submitError }) => (
          <StyledForm onSubmit={handleSubmit}>
            <Headings>
              <Heading>
                <FormattedMessage id="merchant-sign-up.heading.create-your-account" />
              </Heading>
              <Subheading>
                <FormattedMessage id="merchant-sign-up.heading.get-started" />
              </Subheading>
            </Headings>
            <FormRow
              label={intl.formatMessage({ id: 'merchant-sign-up.label.first-name' })}
              labelHtmlFor="firstName"
              labelAlign="center"
            >
              <Field<string | undefined> name="firstName">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="firstName" />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </FormRow>
            <FormRow
              label={intl.formatMessage({ id: 'merchant-sign-up.label.last-name' })}
              labelHtmlFor="lastName"
              labelAlign="center"
            >
              <Field<string | undefined> name="lastName">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="lastName" />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </FormRow>
            <FormRow
              label={intl.formatMessage({ id: 'merchant-sign-up.label.email' })}
              labelHtmlFor="email"
              labelAlign="center"
            >
              <Field<string | undefined> name="email">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="email" disabled={!!userInformation?.email} />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </FormRow>
            <FormRow
              label={intl.formatMessage({ id: 'merchant-sign-up.label.phone-number' })}
              labelHtmlFor="phoneNumber"
              labelAlign="center"
            >
              <Field<string | undefined> name="phoneNumber">
                {field => (
                  <RFFFieldErrorWrapper field={field}>
                    {input => <Input {...input} id="phoneNumber" />}
                  </RFFFieldErrorWrapper>
                )}
              </Field>
            </FormRow>

            <Agreements>
              <Agreement>
                <Field name="termsOfService" type="checkbox">
                  {({ input }) => (
                    <>
                      <InputCheckbox
                        data-test-id="merchant-sign-up-terms-of-service-agreement"
                        {...input}
                        inputClassName="more-visible-checkbox"
                      />
                      <AgreementDescription
                        onClick={() => {
                          input.onChange(!input.checked);
                        }}
                      >
                        <T.Body2>
                          <FormattedMessage id="merchant-sign-up.agreement.terms-of-service" />
                        </T.Body2>
                      </AgreementDescription>
                    </>
                  )}
                </Field>
              </Agreement>
            </Agreements>

            <Button
              type="submit"
              size="large"
              variant="primary"
              stretch
              loading={submitting}
              disabled={hasValidationErrors}
            >
              <FormattedMessage id="merchant-sign-up.button.submit" />
            </Button>

            {submitError && <APIErrorNotification data-test-id="MerchantSignUpFormSubmitError" error={submitError} />}
          </StyledForm>
        )}
      </Form>
    </Root>
  );
};

export default MerchantSignUpForm;
